// ---------------------------------------------------------------------------
// SEO landing-page configuration for the /kinderstemmen/ hierarchy (Dutch).
// One reusable template (ChildVoiceLanguage.jsx) is driven entirely by this
// data. Keys are the Dutch URL slugs (e.g. "nederlands", "amerikaans-engels").
//
// IMPORTANT: `language` MUST stay the exact English database value used to
// filter the directory (do NOT translate it). All display copy is Dutch.
// ---------------------------------------------------------------------------

export const CHILD_VOICES = {
  "nederlands": {
    language: "Dutch", displayName: "Nederlandse",
    h1: "Nederlandse kinderstemacteurs",
    title: "Nederlandse kinderstemacteurs",
    description: "Cast professionele Nederlandse kinderstemacteurs. Beluister Nederlandse kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Bekijk professionele Nederlandse kinderstemacteurs uit Nederland voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Nederlandse kinderstemtalent — onze grootste stemmenpool — en cast met gemak.",
    extra: "Voor een Belgische sound: ontdek onze Belgisch-Vlaamse jonge stemmen, opgenomen met authentieke regionale uitspraak.",
    related: ["belgisch-vlaams"],
  },
  "amerikaans-engels": {
    language: "American English", displayName: "Amerikaans-Engelse",
    h1: "Amerikaans-Engelse kinderstemacteurs",
    title: "Amerikaans-Engelse kinderstemacteurs",
    description: "Cast professionele Amerikaans-Engelse kinderstemacteurs. Beluister Amerikaanse kinderstemvoorbeelden voor reclame, animatie, e-learning en games.",
    intro: "Ontdek professionele Amerikaans-Engelse kinderstemacteurs voor nationale reclames, animatie, apps, e-learning en games. Elke jonge stem hieronder is een echte, geregisseerde kinderacteur — beluister de demo's en zet de juiste stem op je shortlist.",
    extra: "Ons Amerikaans-Engelse kinderstemtalent beslaat een breed scala aan leeftijden en stijlen, van heldere, opgewekte reclamereads tot natuurlijke conversationele narratie en authentieke personagestemmen voor animatie en games.",
    related: ["brits-engels", "australisch-engels"],
  },
  "brits-engels": {
    language: "British English", displayName: "Brits-Engelse",
    h1: "Brits-Engelse kinderstemacteurs",
    title: "Brits-Engelse kinderstemacteurs",
    description: "Vind professionele Brits-Engelse kinderstemacteurs. Beluister Britse kinderstemdemo's voor reclame, animatie, luisterboeken en e-learning.",
    intro: "Bekijk authentieke Brits-Engelse kinderstemacteurs met natuurlijke Britse accenten voor reclames, animatie, luisterboeken, games en educatieve content. Beluister de demo's van elke jonge performer en stel binnen enkele minuten je shortlist samen.",
    extra: "Van received pronunciation tot regionale Britse accenten: onze Britse kinderstemacteurs brengen warmte en helderheid in merkfilms, personagewerk en langere narratie.",
    related: ["amerikaans-engels", "australisch-engels"],
  },
  "australisch-engels": {
    language: "Australian English", displayName: "Australisch-Engelse",
    h1: "Australisch-Engelse kinderstemacteurs",
    title: "Australisch-Engelse kinderstemacteurs",
    description: "Cast Australisch-Engels kinderstemtalent. Beluister echte Australische kinderstemvoorbeelden voor reclame, animatie, games en e-learning.",
    intro: "Maak kennis met echt Australisch-Engels kinderstemtalent met relaxte, natuurlijke Australische accenten voor reclames, animatie, games en leercontent. Beluister demo's en zet de stemmen die bij je briefing passen op je shortlist.",
    extra: "Onze Australische kinderstemmen zijn perfect voor lokale campagnes en internationale producties die een authentieke down-under sound van echte jonge performers nodig hebben.",
    related: ["amerikaans-engels", "brits-engels"],
  },
  "frans": {
    language: "French", displayName: "Franse",
    h1: "Franse kinderstemacteurs",
    title: "Franse kinderstemacteurs",
    description: "Cast professionele Franse kinderstemacteurs. Beluister Franse kinderstemvoorbeelden voor reclame, animatie, e-learning en nasynchronisatie.",
    intro: "Ontdek professionele Franse kinderstemacteurs voor reclames, animatie, e-learning, games en nasynchronisatie. Beluister demo's van ons Franse kinderstemtalent en vind de juiste jonge stem voor je project.",
    extra: "Of je nu een speelse personagestem voor animatie, een oprechte read voor een merkfilm of heldere narratie voor e-learning nodig hebt: ons Franse jonge stemtalent levert geregisseerde, broadcast-klare vertolkingen.",
    related: ["belgisch-vlaams"],
  },
  "duits": {
    language: "German", displayName: "Duitse",
    h1: "Duitse kinderstemacteurs",
    title: "Duitse kinderstemacteurs",
    description: "Vind professionele Duitse kinderstemacteurs. Beluister Duitse kinderstemdemo's voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Cast authentieke Duitse kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en educatieve producties. Beluister demo's van ons Duitse kinderstemtalent en zet de perfecte jonge stem op je shortlist.",
    extra: "Onze Duitstalige kinderstemmen werken aan tv- en radiocampagnes, personage-animatie en lokalisatieprojecten, altijd opgenomen met professionele regie.",
    related: [],
  },
  "spaans": {
    language: "Spanish", displayName: "Spaanse",
    h1: "Spaanse kinderstemacteurs",
    title: "Spaanse kinderstemacteurs",
    description: "Cast Europees-Spaanse kinderstemacteurs. Beluister Spaanse kinderstemvoorbeelden voor reclame, animatie, games, nasynchronisatie en e-learning.",
    intro: "Ontdek Europees-Spaanse (Castiliaanse) kinderstemacteurs voor reclames, animatie, games, nasynchronisatie en e-learning. Beluister demo's van ons Spaanse kinderstemtalent en cast met vertrouwen.",
    extra: "Op zoek naar een Latijns-Amerikaanse sound? We vertegenwoordigen ook specifiek Latijns-Amerikaans-Spaans jong stemtalent voor producties gericht op het Amerikaanse continent.",
    related: ["latijns-amerikaans-spaans"],
  },
  "latijns-amerikaans-spaans": {
    language: "Latin American Spanish", displayName: "Latijns-Amerikaans-Spaanse",
    h1: "Latijns-Amerikaans-Spaanse kinderstemacteurs",
    title: "Latijns-Amerikaans-Spaanse kinderstemacteurs",
    description: "Vind Latijns-Amerikaans-Spaanse kinderstemacteurs. Beluister neutrale Latijns-Spaanse kinderstemdemo's voor reclame, animatie, nasynchronisatie en e-learning.",
    intro: "Cast Latijns-Amerikaans-Spaanse kinderstemacteurs met neutrale en regionale accenten voor reclames, animatie, nasynchronisatie en e-learning in Noord- en Zuid-Amerika. Beluister demo's en zet de juiste jonge stem op je shortlist.",
    extra: "Voor campagnes gericht op Spanje: ontdek ons Europees-Spaanse kinderstemtalent voor een authentieke Castiliaanse sound.",
    related: ["spaans"],
  },
  "italiaans": {
    language: "Italian", displayName: "Italiaanse",
    h1: "Italiaanse kinderstemacteurs",
    title: "Italiaanse kinderstemacteurs",
    description: "Cast professionele Italiaanse kinderstemacteurs. Beluister Italiaanse kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Bekijk professionele Italiaanse kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en leercontent. Beluister demo's van ons Italiaanse kinderstemtalent en vind je ideale jonge stem.",
    extra: "Onze Italiaanse jonge performers brengen een expressieve, natuurlijke voordracht in merkcampagnes, personage-animatie en lokalisatieprojecten.",
    related: [],
  },
  "portugees-portugal": {
    language: "Portuguese (Portugal)", displayName: "Europees-Portugese",
    h1: "Europees-Portugese kinderstemacteurs",
    title: "Europees-Portugese kinderstemacteurs",
    description: "Cast Europees-Portugese kinderstemacteurs. Beluister Portugese kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Ontdek Europees-Portugese kinderstemacteurs uit Portugal voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's en zet de juiste jonge stem voor je productie op je shortlist.",
    extra: "Een Braziliaanse sound nodig? We vertegenwoordigen ook specifiek Braziliaans-Portugees kinderstemtalent voor producties gericht op Brazilië.",
    related: ["portugees-brazilie"],
  },
  "portugees-brazilie": {
    language: "Portuguese (Brazil)", displayName: "Braziliaans-Portugese",
    h1: "Braziliaans-Portugese kinderstemacteurs",
    title: "Braziliaans-Portugese kinderstemacteurs",
    description: "Vind Braziliaans-Portugese kinderstemacteurs. Beluister Braziliaanse kinderstemdemo's voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Cast Braziliaans-Portugese kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning gericht op het publiek in Brazilië. Beluister demo's van ons jonge stemtalent en stel je shortlist samen.",
    extra: "Voor producties gericht op Portugal: ontdek onze Europees-Portugese kinderstemacteurs voor een authentieke continentale sound.",
    related: ["portugees-portugal"],
  },
  "belgisch-vlaams": {
    language: "Belgian Flemish", displayName: "Belgisch-Vlaamse",
    h1: "Belgisch-Vlaamse kinderstemacteurs",
    title: "Belgisch-Vlaamse kinderstemacteurs",
    description: "Vind Belgisch-Vlaamse kinderstemacteurs. Beluister Vlaamse kinderstemdemo's voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Cast Belgisch-Vlaamse kinderstemacteurs met authentieke Vlaamse uitspraak voor reclames, animatie, nasynchronisatie en e-learning in België. Beluister demo's en zet de juiste jonge stem op je shortlist.",
    extra: "Voor de Nederlandse markt: ontdek onze bredere pool Nederlandse kinderstemacteurs.",
    related: ["nederlands"],
  },
  "grieks": {
    language: "Greek", displayName: "Griekse",
    h1: "Griekse kinderstemacteurs",
    title: "Griekse kinderstemacteurs",
    description: "Cast Griekse kinderstemacteurs. Beluister Griekse kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Ontdek Griekse kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Griekse kinderstemtalent en vind de juiste jonge stem voor je project.",
    extra: "Onze Griekse jonge performers leveren natuurlijke, geregisseerde reads voor merkcampagnes, personagewerk en lokalisatie.",
    related: [],
  },
  "deens": {
    language: "Danish", displayName: "Deense",
    h1: "Deense kinderstemacteurs",
    title: "Deense kinderstemacteurs",
    description: "Cast Deens kinderstemtalent. Vraag Deense kinderstemmen aan voor reclame, animatie, nasynchronisatie, games en e-learning uit ons netwerk.",
    intro: "Op zoek naar Deens kinderstemtalent voor reclames, animatie, nasynchronisatie, games of e-learning? Ons internationale netwerk reikt veel verder dan de stemmen die online staan — vertel ons je briefing en wij casten de juiste jonge Deense stem voor je.",
    extra: "We werven regelmatig Deense kinderstemmen voor Scandinavische en internationale producties. Stuur ons je script en we stellen geschikt jong talent voor.",
    related: ["zweeds", "noors"],
  },
  "fins": {
    language: "Finnish", displayName: "Finse",
    h1: "Finse kinderstemacteurs",
    title: "Finse kinderstemacteurs",
    description: "Cast Finse kinderstemacteurs. Beluister Finse kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Bekijk Finse kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Finse kinderstemtalent en zet de perfecte jonge stem op je shortlist.",
    extra: "Onze Finse jonge performers brengen een authentieke, natuurlijke voordracht in Noordse en internationale campagnes.",
    related: ["zweeds", "noors"],
  },
  "noors": {
    language: "Norwegian", displayName: "Noorse",
    h1: "Noorse kinderstemacteurs",
    title: "Noorse kinderstemacteurs",
    description: "Cast Noorse kinderstemacteurs. Beluister Noorse kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Ontdek Noorse kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Noorse kinderstemtalent en cast de juiste jonge stem.",
    extra: "Onze Noorse jonge stemmen zijn ideaal voor Scandinavische lokalisatie en internationale producties die een authentieke Noordse sound nodig hebben.",
    related: ["zweeds", "fins"],
  },
  "zweeds": {
    language: "Swedish", displayName: "Zweedse",
    h1: "Zweedse kinderstemacteurs",
    title: "Zweedse kinderstemacteurs",
    description: "Cast Zweedse kinderstemacteurs. Beluister Zweedse kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Bekijk Zweedse kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Zweedse kinderstemtalent en zet de juiste jonge stem op je shortlist.",
    extra: "Onze Zweedse jonge performers leveren natuurlijke, geregisseerde reads voor Noordse en wereldwijde campagnes.",
    related: ["noors", "fins"],
  },
  "pools": {
    language: "Polish", displayName: "Poolse",
    h1: "Poolse kinderstemacteurs",
    title: "Poolse kinderstemacteurs",
    description: "Cast Pools kinderstemtalent. Vraag Poolse kinderstemmen aan voor reclame, animatie, nasynchronisatie, games en e-learning uit ons netwerk.",
    intro: "Pools kinderstemtalent nodig voor reclames, animatie, nasynchronisatie, games of e-learning? Ons internationale netwerk reikt verder dan de stemmen die online staan — stuur ons je briefing en wij casten de juiste jonge Poolse stem.",
    extra: "We werven regelmatig Poolse kinderstemmen voor Europese en internationale producties. Deel je script en we stellen geschikt jong talent voor.",
    related: ["russisch", "roemeens"],
  },
  "russisch": {
    language: "Russian", displayName: "Russische",
    h1: "Russische kinderstemacteurs",
    title: "Russische kinderstemacteurs",
    description: "Cast Russische kinderstemacteurs. Beluister Russische kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Ontdek Russische kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Russische kinderstemtalent en vind de juiste jonge stem.",
    extra: "Onze Russische jonge performers leveren expressieve, geregisseerde reads voor personage-animatie, lokalisatie en merkwerk.",
    related: [],
  },
  "roemeens": {
    language: "Romanian", displayName: "Roemeense",
    h1: "Roemeense kinderstemacteurs",
    title: "Roemeense kinderstemacteurs",
    description: "Cast Roemeense kinderstemacteurs. Beluister Roemeense kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Bekijk Roemeense kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Roemeense kinderstemtalent en zet de perfecte jonge stem op je shortlist.",
    extra: "Onze Roemeense jonge stemmen brengen een natuurlijke, authentieke voordracht in regionale en internationale producties.",
    related: [],
  },
  "turks": {
    language: "Turkish", displayName: "Turkse",
    h1: "Turkse kinderstemacteurs",
    title: "Turkse kinderstemacteurs",
    description: "Cast Turkse kinderstemacteurs. Beluister Turkse kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Ontdek Turkse kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Turkse kinderstemtalent en cast de juiste jonge stem.",
    extra: "Onze Turkse jonge performers leveren natuurlijke, geregisseerde reads voor merkcampagnes, personagewerk en lokalisatie.",
    related: [],
  },
  "arabisch": {
    language: "Arabic", displayName: "Arabische",
    h1: "Arabische kinderstemacteurs",
    title: "Arabische kinderstemacteurs",
    description: "Cast Arabische kinderstemacteurs. Beluister Arabische kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Bekijk Arabische kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Arabische kinderstemtalent en zet de juiste jonge stem voor je productie op je shortlist.",
    extra: "Onze Arabische jonge performers dekken Modern Standaardarabisch en regionale behoeften voor broadcast, personage-animatie en lokalisatieprojecten.",
    related: [],
  },
  "hebreeuws": {
    language: "Hebrew", displayName: "Hebreeuwse",
    h1: "Hebreeuwse kinderstemacteurs",
    title: "Hebreeuwse kinderstemacteurs",
    description: "Cast Hebreeuws kinderstemtalent. Vraag Israëlisch-Hebreeuwse kinderstemmen aan voor reclame, animatie, nasynchronisatie, games en e-learning uit ons netwerk.",
    intro: "Op zoek naar Hebreeuws kinderstemtalent voor reclames, animatie, nasynchronisatie, games of e-learning? Ons internationale netwerk reikt verder dan de stemmen die online staan — stuur ons je briefing en wij casten de juiste jonge Hebreeuwse stem.",
    extra: "We werven regelmatig Israëlisch-Hebreeuwse kinderstemmen voor regionale en internationale producties. Deel je script en we stellen geschikt jong talent voor.",
    related: [],
  },
  "afrikaans": {
    language: "Afrikaans", displayName: "Afrikaanse",
    h1: "Afrikaanse kinderstemacteurs",
    title: "Afrikaanse kinderstemacteurs",
    description: "Cast Afrikaanse kinderstemacteurs. Beluister Zuid-Afrikaanse Afrikaanse kinderstemvoorbeelden voor reclame, animatie, games en e-learning.",
    intro: "Ontdek Afrikaanse kinderstemacteurs uit Zuid-Afrika voor reclames, animatie, games en e-learning. Beluister demo's van ons Afrikaanse kinderstemtalent en zet de juiste jonge stem op je shortlist.",
    extra: "Onze Afrikaanse jonge performers leveren warme, natuurlijke reads voor lokale campagnes en internationale producties.",
    related: [],
  },
  "chinees": {
    language: "Chinese", displayName: "Chinese",
    h1: "Chinese kinderstemacteurs",
    title: "Chinese kinderstemacteurs",
    description: "Cast Chinese (Mandarijn) kinderstemacteurs. Beluister Chinese kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Bekijk Chinese (Mandarijn) kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Chinese kinderstemtalent en vind de juiste jonge stem.",
    extra: "Onze Chinese jonge performers brengen een authentieke, natuurlijke voordracht in merkcampagnes, personage-animatie en lokalisatie voor Aziatische en wereldwijde doelgroepen.",
    related: ["japans", "vietnamees"],
  },
  "japans": {
    language: "Japanese", displayName: "Japanse",
    h1: "Japanse kinderstemacteurs",
    title: "Japanse kinderstemacteurs",
    description: "Cast Japanse kinderstemacteurs. Beluister Japanse kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Ontdek Japanse kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Japanse kinderstemtalent en cast de juiste jonge stem.",
    extra: "Onze Japanse jonge performers leveren expressieve, geregisseerde reads die perfect zijn voor animatie, games en merklokalisatie.",
    related: ["chinees", "vietnamees"],
  },
  "vietnamees": {
    language: "Vietnamese", displayName: "Vietnamese",
    h1: "Vietnamese kinderstemacteurs",
    title: "Vietnamese kinderstemacteurs",
    description: "Cast Vietnamese kinderstemacteurs. Beluister Vietnamese kinderstemvoorbeelden voor reclame, animatie, nasynchronisatie, games en e-learning.",
    intro: "Bekijk Vietnamese kinderstemacteurs voor reclames, animatie, nasynchronisatie, games en e-learning. Beluister demo's van ons Vietnamese kinderstemtalent en zet de juiste jonge stem op je shortlist.",
    extra: "Onze Vietnamese jonge performers brengen een natuurlijke, authentieke voordracht in regionale en internationale producties.",
    related: ["chinees", "japans"],
  },
};

// Ordered list of slugs for the "Browse by language" internal-linking section.
export const CHILD_VOICE_ORDER = Object.keys(CHILD_VOICES);

export const getChildVoice = (slug) => CHILD_VOICES[slug] || null;

export const childVoicePath = (slug) => `/kinderstemmen/${slug}`;

// Canonical SEO path for a talent profile: /kinderstemmen/{taal-slug}/{naam-slug}
export const talentPath = (t) => {
  if (!t) return "/kinderstemmen";
  return `/kinderstemmen/${dutchSlugForLanguage(t.language)}/${t.slug || t.id}`;
};

const _slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Reverse lookups: English DB language value -> Dutch URL slug.
const _byLanguage = {};
const _byEnglishSlug = {};
for (const [slug, cfg] of Object.entries(CHILD_VOICES)) {
  _byLanguage[cfg.language] = slug;
  _byEnglishSlug[_slugify(cfg.language)] = slug;
}

// language name ("Dutch") -> "nederlands". Falls back to a slugified value.
export const dutchSlugForLanguage = (language) => _byLanguage[language] || _slugify(language);

// old English URL slug ("dutch") -> Dutch slug ("nederlands").
export const englishSlugToDutch = (slug) => _byEnglishSlug[slug] || slug;
